import { BASE_CELL_COUNT, GRID_SIZE } from "../gridSize";
import { LINE_OPTIONS, lineRows } from "../rules/lines";
import { BLANK, COLLECT, EMPTY_POTS, MYSTERY, MYSTERY_JACKPOT, isBonusSymbolId, type PotKind, type PotLevels } from "../rules/symbols";
import { blankBoard } from "./holdRules";
import { betOf, coinWorth, runBonus, settleSpin, type BonusDecisions, type OfflineServerState } from "./holdGame";
import type { Boosters, RoundOutcome } from "./roundTypes";

/**
 * A round written out instead of drawn — the dev menu's and the e2e suite's rounds — played through the same rules as
 * the random server (`holdGame.ts`), so a script states only what lands and the rules do the rest (holds, collects, the
 * meter, LIFE, multipliers, the pay). Tokens: `.` blank, `c<n>` a coin worth n tenths of the bet its values follow (a
 * line's), `MINI` / `MINOR` / `MAJOR`, `MYS`, `COL`. A script is written for every line (decision 56) and plays at any
 * count: fewer lines only take triggers away — a line no longer active, a gem in a row no longer played — and then its
 * bonus is not played.
 */
export interface RoundScript {
  /** The base board's three rows, four tokens each. */
  board: readonly (readonly string[])[];
  /**
   * The least each pot starts from (else empty). A round starts from the pots the last round left at the bet (the
   * server holds them per bet), each at least this: its gems stack on them (a full pot still takes one, staying full),
   * so a pot fills to the top whichever scripts bring its gems, and stays full until a script plays the bonus — a full
   * pot starts it at the server's draw, and a script's `bonus` is that draw.
   */
  potsBefore?: Partial<PotLevels>;
  /** The gems the spin's coins carry. */
  marks?: readonly { cell: number; pot: PotKind }[];
  /** A bought bonus: its boosters. */
  buy?: Boosters;
  /** The bonus it plays (a covered line's, a buy's, or a full pot's at the server's draw). */
  bonus?: {
    /** What lands per respin (1-based): cell → token. A respin not listed lands nothing. */
    lands?: Readonly<Record<number, Readonly<Record<number, string>>>>;
    /** MULTI's hits per respin (a cell may repeat). */
    drops?: Readonly<Record<number, readonly number[]>>;
    /** What each mystery reveals: cell → token (a coin or a jackpot coin; a jackpot coin for a mystery jackpot, `MJP`). */
    reveals?: Readonly<Record<number, string>>;
  };
}

/** The drops of a MULTI booster a script writes none for (one its stacked pots brought). */
const DEFAULT_DROPS = 2;

/** A token as a board id at `betMinor`. */
export function tokenId(token: string, betMinor: bigint): string {
  if (token === ".") return BLANK;
  if (token === "MYS") return MYSTERY;
  if (token === "MJP") return MYSTERY_JACKPOT;
  if (token === "COL") return COLLECT;
  if (token === "MINI" || token === "MINOR" || token === "MAJOR") return token;
  const coin = /^c(\d+)$/.exec(token);
  if (coin) return coinWorth(betMinor, Number(coin[1]));
  throw new Error(`scriptedRound: unknown token "${token}"`);
}

/** Plays `script` at `betMinor` over `lines` lines; the server state's pots and bet follow it as they would a random round. */
export function scriptedRound(betMinor: string, lines: number, script: RoundScript, state: OfflineServerState): RoundOutcome {
  const { stake, lineBet, valueBet: bet, rows } = betOf(betMinor, lines, script.buy !== undefined);
  if (script.board.length !== 3 || script.board.some((row) => row.length !== GRID_SIZE.columns)) throw new Error("scriptedRound: the board is three rows of four");
  const board = blankBoard();
  script.board.flat().forEach((token, index) => {
    if (index < BASE_CELL_COUNT) board[index] = tokenId(token, bet);
  });
  const marks = (script.marks ?? []).map((m) => ({ index: m.cell, pot: m.pot }));
  const own: PotLevels = { ...EMPTY_POTS, ...script.potsBefore };
  // The script as written (its own pots, every line): its board starts the bonus exactly when it plays one.
  const potTrigger = script.bonus !== undefined;
  const written = settleSpin({ board, potsBefore: own, marks, buy: script.buy ?? null, rows: lineRows(Math.max(...LINE_OPTIONS)), potTrigger });
  if (Boolean(written.trigger) !== Boolean(script.bonus)) throw new Error(`scriptedRound: the board ${written.trigger ? "starts" : "does not start"} the bonus, the script ${script.bonus ? "plays one" : "plays none"}`);
  // Played on the pots the last round left at the bet, each at least the script's own: its gems stack on them.
  const held = state.lastLineBetMinor === lineBet.toString() ? state.pots : EMPTY_POTS;
  const potsBefore: PotLevels = { life: Math.max(held.life, own.life), multi: Math.max(held.multi, own.multi), grow: Math.max(held.grow, own.grow) };
  const spin = settleSpin({ board, potsBefore, marks, buy: script.buy ?? null, rows, potTrigger });
  const plan = script.bonus;
  const decisions: BonusDecisions = {
    land: (respin) => new Map(Object.entries(plan?.lands?.[respin] ?? {}).map(([cell, token]) => [Number(cell), tokenId(token, bet)])),
    // Its drops; with none written, a MULTI booster (one the stacked pots brought) throws once, on the first respin,
    // onto the first coins in play (MULTI erupts in every bonus it boosts).
    erupt: (respin, _mayBeLast, cellsInPlay) => {
      const written = plan?.drops;
      if (written) return written[respin] ?? [];
      if (respin !== 1 || !spin.trigger?.boosters.multi) return [];
      const coins = cellsInPlay.filter((index) => isBonusSymbolId(spin.board[index]!));
      return (coins.length > 0 ? coins : cellsInPlay).slice(0, DEFAULT_DROPS);
    },
    reveal: (index) => {
      const token = plan?.reveals?.[index];
      if (token === undefined) throw new Error(`scriptedRound: the script reveals no symbol for the mystery in cell ${index}`);
      return tokenId(token, bet);
    },
  };
  const bonus = spin.trigger ? runBonus(bet, stake, spin.board, spin.trigger.boosters, decisions) : null;
  state.lastLineBetMinor = lineBet.toString();
  state.pots = bonus ? EMPTY_POTS : spin.potsAfter;
  return { betMinor, lines, lineBetMinor: lineBet.toString(), valueBetMinor: bet.toString(), spin, bonus, winMinor: bonus?.end.totalMinor ?? "0" };
}
